import React, { useEffect } from 'react';
import { LocalizedLink } from '../components/LocalizedLink';
import { useAdminStore } from '../stores/adminStore';
import { useTranslation } from '../hooks/useTranslation';
import { toLocalizedFormat, UNIVERSAL_DATE_FORMAT } from '@dutchinvoice/shared-utils';
import { useInvoiceStore } from '../stores/invoiceStore';
import { format } from 'date-fns';
import { format as timeAgo } from 'timeago.js';

export const AdminPage: React.FC = () => {
  const { t } = useTranslation();
  const language = useInvoiceStore(state => state.settings.language);
  const { users, loading, error, fetchUsers } = useAdminStore();

  useEffect(() => {
    // Fetch users data when component mounts
    fetchUsers();
  }, [fetchUsers]);

  const activeToday = users.filter(u => {
    const lastActiveDate = new Date(u.lastActive);
    const today = new Date();
    return lastActiveDate.toDateString() === today.toDateString();
  }).length;

  return (
    <div className="min-h-screen bg-gray-50/50 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <LocalizedLink
              to="/"
              className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('admin.backToHome')}
            </LocalizedLink>
            <h1 className="text-2xl font-semibold text-gray-900">{t('admin.title')}</h1>
          </div>
          <div>
            <LocalizedLink
              to="/admin/email-queue"
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {t('admin.emails.viewQueue')}
            </LocalizedLink>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-6">
            <div className="text-sm font-medium text-gray-500 mb-1">{t('admin.users.title')}</div>
            <div className="text-3xl font-semibold text-gray-900">{users.length}</div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-6">
            <div className="text-sm font-medium text-gray-500 mb-1">{t('admin.users.activeToday')}</div>
            <div className="text-3xl font-semibold text-gray-900">{activeToday}</div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-6">
            <div className="text-sm font-medium text-gray-500 mb-1">{t('admin.users.totalInvoices')}</div>
            <div className="text-3xl font-semibold text-gray-900">
              {users.reduce((acc, user) => acc + user.invoiceCount, 0)}
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-medium text-gray-900">{t('admin.users.title')}</h2>
          </div>
          
          {loading ? (
            <div className="p-6 text-center text-gray-500">{t('common.loading')}</div>
          ) : error ? (
            <div className="p-6 text-center text-red-500">{error}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.users.email')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.users.company')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.users.invoices')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.users.joined')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.users.lastActive')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.users.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {users.map((user) => {
                    const createdAtDate = format(new Date(user.createdAt), UNIVERSAL_DATE_FORMAT);
                    const lastActiveDate = format(new Date(user.lastActive), UNIVERSAL_DATE_FORMAT);
                    
                    return (
                      <tr key={user.uid} className="hover:bg-gray-50/50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.defaultCompany || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.invoiceCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {toLocalizedFormat(createdAtDate, language)}
                          <div className="text-xs text-gray-400 mt-0.5">
                            {timeAgo(user.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {toLocalizedFormat(lastActiveDate, language)}
                          <div className="text-xs text-gray-400 mt-0.5">
                            {timeAgo(user.lastActive)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <div className="flex justify-end space-x-4">
                            <LocalizedLink
                              to={`/admin/user/${user.uid}/invoices`}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              {t('admin.users.viewInvoices')}
                            </LocalizedLink>
                            <LocalizedLink
                              to={`/admin/user/${user.uid}/companies`}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              {t('admin.users.viewCompanies')}
                            </LocalizedLink>
                            <LocalizedLink
                              to={`/admin/user/${user.uid}/settings`}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              {t('admin.users.viewSettings')}
                            </LocalizedLink>
                            <LocalizedLink
                              to={`/admin/user/${user.uid}/schedules`}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              {t('admin.users.viewSchedules')}
                            </LocalizedLink>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 